/**
 * Performance Monitor Hook
 * Tracks component rendering performance and provides optimization insights
 * Helps identify components that need React.memo or other optimizations
 */

'use client';

import { useEffect, useRef, useCallback } from 'react';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface PerformanceMetrics {
  /** Component name */
  componentName: string;
  /** Render count */
  renderCount: number;
  /** Average render time in milliseconds */
  averageRenderTime: number;
  /** Total render time in milliseconds */
  totalRenderTime: number;
  /** Last render timestamp */
  lastRenderTime: number;
  /** Peak render time in milliseconds */
  peakRenderTime: number;
  /** Minimum render time in milliseconds */
  minRenderTime: number;
}

export interface PerformanceMonitorOptions {
  /** Whether to enable performance monitoring */
  enabled?: boolean;
  /** Threshold for warning about slow renders (ms) */
  warningThreshold?: number;
  /** Threshold for error about very slow renders (ms) */
  errorThreshold?: number;
  /** Whether to log to console */
  logToConsole?: boolean;
  /** Custom logger function */
  logger?: (metrics: PerformanceMetrics) => void;
}

// =============================================================================
// PERFORMANCE MONITOR HOOK
// =============================================================================

export function usePerformanceMonitor(
  componentName: string,
  options: PerformanceMonitorOptions = {}
) {
  const {
    enabled = process.env.NODE_ENV === 'development',
    warningThreshold = 16, // 60fps = ~16ms per frame
    errorThreshold = 50,   // 20fps = ~50ms per frame
    logToConsole = true,
    logger,
  } = options;

  const metricsRef = useRef<PerformanceMetrics>({
    componentName,
    renderCount: 0,
    averageRenderTime: 0,
    totalRenderTime: 0,
    lastRenderTime: 0,
    peakRenderTime: 0,
    minRenderTime: Infinity,
  });

  const startTimeRef = useRef<number>(0);

  // Start performance measurement
  const startMeasurement = useCallback(() => {
    if (!enabled) return;
    startTimeRef.current = performance.now();
  }, [enabled]);

  // End performance measurement
  const endMeasurement = useCallback(() => {
    if (!enabled) return;

    const endTime = performance.now();
    const renderTime = endTime - startTimeRef.current;

    const metrics = metricsRef.current;
    metrics.renderCount++;
    metrics.totalRenderTime += renderTime;
    metrics.averageRenderTime = metrics.totalRenderTime / metrics.renderCount;
    metrics.lastRenderTime = renderTime;
    metrics.peakRenderTime = Math.max(metrics.peakRenderTime, renderTime);
    metrics.minRenderTime = Math.min(metrics.minRenderTime, renderTime);

    // Log performance warnings
    if (renderTime > errorThreshold) {
      console.error(
        `🚨 Performance Error: ${componentName} took ${renderTime.toFixed(2)}ms to render (threshold: ${errorThreshold}ms)`
      );
    } else if (renderTime > warningThreshold) {
      console.warn(
        `⚠️ Performance Warning: ${componentName} took ${renderTime.toFixed(2)}ms to render (threshold: ${warningThreshold}ms)`
      );
    }

    // Log to console if enabled
    if (logToConsole && metrics.renderCount % 10 === 0) {
      console.log(`📊 ${componentName} Performance:`, {
        renderCount: metrics.renderCount,
        averageRenderTime: metrics.averageRenderTime.toFixed(2) + 'ms',
        lastRenderTime: metrics.lastRenderTime.toFixed(2) + 'ms',
        peakRenderTime: metrics.peakRenderTime.toFixed(2) + 'ms',
        minRenderTime: metrics.minRenderTime.toFixed(2) + 'ms',
      });
    }

    // Call custom logger if provided
    if (logger) {
      logger(metrics);
    }
  }, [enabled, componentName, warningThreshold, errorThreshold, logToConsole, logger]);

  // Get current metrics
  const getMetrics = useCallback(() => {
    return { ...metricsRef.current };
  }, []);

  // Reset metrics
  const resetMetrics = useCallback(() => {
    metricsRef.current = {
      componentName,
      renderCount: 0,
      averageRenderTime: 0,
      totalRenderTime: 0,
      lastRenderTime: 0,
      peakRenderTime: 0,
      minRenderTime: Infinity,
    };
  }, [componentName]);

  // Start measurement on mount
  useEffect(() => {
    startMeasurement();
  }, [startMeasurement]);

  // End measurement after render
  useEffect(() => {
    endMeasurement();
  }, [endMeasurement]);

  return {
    startMeasurement,
    endMeasurement,
    getMetrics,
    resetMetrics,
    metrics: metricsRef.current,
  };
}

// =============================================================================
// PERFORMANCE MONITOR HOC
// =============================================================================

export function withPerformanceMonitor<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options: PerformanceMonitorOptions = {}
) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const PerformanceMonitoredComponent = (props: P) => {
    const { startMeasurement, endMeasurement } = usePerformanceMonitor(displayName, options);

    useEffect(() => {
      startMeasurement();
    });

    useEffect(() => {
      endMeasurement();
    });

    return <WrappedComponent {...props} />;
  };

  PerformanceMonitoredComponent.displayName = `withPerformanceMonitor(${displayName})`;

  return PerformanceMonitoredComponent;
}

// =============================================================================
// PERFORMANCE UTILITIES
// =============================================================================

export function measurePerformance<T extends (...args: any[]) => any>(
  fn: T,
  name: string
): T {
  return ((...args: Parameters<T>) => {
    const start = performance.now();
    const result = fn(...args);
    const end = performance.now();

    console.log(`⏱️ ${name} took ${(end - start).toFixed(2)}ms`);

    return result;
  }) as T;
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}
